import { useMemo, useState } from "react";
import { useSubs } from "@/context/SubsContext";
import {
  CATEGORIES,
  categoryLabel,
  formatDateFR,
  formatEUR,
  frequencyLabel,
  toMonthly,
  toYearly,
} from "@/lib/subscriptions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import SubscriptionDialog from "@/components/SubscriptionDialog";
import { Plus, Search, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function Subscriptions() {
  const { subs, removeSub, loading } = useSubs();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("renewal");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const filtered = useMemo(() => {
    let list = [...subs];
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (s) => s.name.toLowerCase().includes(q) || (s.note || "").toLowerCase().includes(q)
      );
    }
    if (category !== "all") list = list.filter((s) => s.category === category);
    if (sort === "price") {
      list.sort((a, b) => toMonthly(b.price, b.frequency) - toMonthly(a.price, a.frequency));
    } else if (sort === "renewal") {
      list.sort((a, b) => (a.next_renewal || "").localeCompare(b.next_renewal || ""));
    } else if (sort === "name") {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
  }, [subs, query, category, sort]);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="subs-root">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <Input
            placeholder="Rechercher un abonnement…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="sub-search"
            className="pl-9 rounded-full bg-white border-gray-200 focus-visible:ring-emerald-500"
          />
        </div>
        <div className="flex gap-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="sub-filter-category" className="w-[180px] rounded-full bg-white">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sub-sort" className="w-[160px] rounded-full bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="renewal">Trier : renouvellement</SelectItem>
              <SelectItem value="price">Trier : prix</SelectItem>
              <SelectItem value="name">Trier : nom</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={openAdd}
            data-testid="add-sub-button"
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 gap-2"
          >
            <Plus size={16} /> Ajouter
          </Button>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-gray-100 shadow-soft overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-6 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 border-b border-gray-100 bg-gray-50/50">
          <div className="col-span-4">Service</div>
          <div className="col-span-2">Catégorie</div>
          <div className="col-span-2">Prix mensuel</div>
          <div className="col-span-2">Renouvellement</div>
          <div className="col-span-1">Statut</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm" data-testid="subs-empty">
            Aucun abonnement trouvé.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            <AnimatePresence>
              {filtered.map((s, i) => {
                const monthly = toMonthly(s.price, s.frequency);
                const yearly = toYearly(s.price, s.frequency);
                return (
                  <motion.li
                    key={s.id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0, transition: { delay: i * 0.03 } }}
                    exit={{ opacity: 0 }}
                    data-testid={`sub-row-${s.id}`}
                    className="grid grid-cols-12 items-center px-6 py-4 hover:bg-gray-50/60"
                  >
                    <div className="col-span-12 md:col-span-4 flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <span className="font-heading font-bold text-emerald-700">
                          {s.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{s.name}</div>
                        {s.note && <div className="text-xs text-gray-500 truncate">{s.note}</div>}
                        {s.is_demo && (
                          <Badge variant="outline" className="mt-1 text-[10px] rounded-full border-emerald-200 text-emerald-700">
                            Démo
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-2 text-sm text-gray-600">
                      {categoryLabel(s.category)}
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <div className="font-semibold text-gray-900">{formatEUR(monthly)}</div>
                      <div className="text-xs text-gray-500">
                        {formatEUR(yearly)} / an · {frequencyLabel(s.frequency)}
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-2 text-sm text-gray-600">
                      {formatDateFR(s.next_renewal)}
                    </div>
                    <div className="col-span-4 md:col-span-1">
                      <Badge
                        className={`rounded-full ${
                          s.status === "actif"
                            ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {s.status === "actif" ? "Actif" : "Résilié"}
                      </Badge>
                    </div>
                    <div className="col-span-2 md:col-span-1 flex justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`sub-menu-${s.id}`}
                            className="rounded-full h-8 w-8"
                          >
                            <MoreHorizontal size={18} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl">
                          <DropdownMenuItem onClick={() => openEdit(s)} data-testid={`sub-edit-${s.id}`}>
                            <Pencil size={14} className="mr-2" /> Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setToDelete(s)}
                            data-testid={`sub-delete-${s.id}`}
                            className="text-red-600 focus:text-red-700"
                          >
                            <Trash2 size={14} className="mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>
        )}
      </div>

      <SubscriptionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet abonnement ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'abonnement « {toDelete?.name} » sera définitivement supprimé de SubSave. Aucune résiliation n'est effectuée auprès du fournisseur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-sub"
              className="rounded-full bg-red-600 hover:bg-red-700"
              onClick={async () => {
                const id = toDelete.id;
                setToDelete(null);
                await removeSub(id);
              }}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
